import React, { useContext, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CopyIcon,
  CheckIcon,
  TrashIcon,
  LinkIcon,
  CircleNotchIcon,
  UsersIcon,
  CalendarIcon,
  ArrowSquareOutIcon,
  PencilIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useShareableLinks,
  useDeleteShareableLink,
  getShareableLinkUrl,
  ShareableLink,
  ShareableLinkWithCreator,
} from 'src/api/core/useShareableLinks';
import { Card, CardContent } from '@/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { EditShareableLinkModal } from './EditShareableLinkModal';
import { T, Var, useGT } from 'gt-react';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from '@/utils/user';

interface SharedLinksManagerProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle: string;
  onCreateNew: () => void;
}

export function SharedLinksManager({
  open,
  onClose,
  reportId,
  reportTitle,
  onCreateNew,
}: SharedLinksManagerProps) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingLink, setEditingLink] = useState<ShareableLink | null>(null);

  const { data: linksData, isLoading } = useShareableLinks({
    resourceType: `report:${reportId}`,
    enabled: open,
    // Admins see every user's links for this report, along with who created each.
    all: isAdminUser,
  });

  const links = linksData?.shareableLinks || [];

  function handleCopyLink(link: ShareableLink) {
    const url = getShareableLinkUrl(link.token);
    navigator.clipboard.writeText(url);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function formatMetadata(metadata: Record<string, unknown>): string {
    const parts: string[] = [];

    if (metadata.year) {
      parts.push(String(metadata.year));
    }

    // Use sharedFilters for a generic, human-readable summary
    const sharedFilters = metadata.sharedFilters as
      | Array<{
          key: string;
          label: string;
          options: Array<{ id: string; label: string }>;
        }>
      | undefined;

    if (sharedFilters && sharedFilters.length > 0) {
      sharedFilters.forEach((filter) => {
        const labels = filter.options.map((o) => o.label);
        if (labels.length <= 3) {
          parts.push(`${filter.label}: ${labels.join(', ')}`);
        } else {
          parts.push(
            gt('{label}: {count} selected', {
              label: filter.label,
              count: labels.length,
              context: 'shared link filter summary with count',
            }),
          );
        }
      });
    } else {
      // Legacy format fallback
      if (metadata.lineType) {
        parts.push(
          gt('Line Type: {lineType}', {
            lineType: String(metadata.lineType),
            context: 'shared link filter summary',
          }),
        );
      }
      if (
        Array.isArray(metadata.divisionIds) &&
        metadata.divisionIds.length > 0
      ) {
        const n = metadata.divisionIds.length;
        parts.push(
          n === 1
            ? gt('1 division', { context: 'shared link filter summary' })
            : gt('{count} divisions', {
                count: n,
                context: 'shared link filter summary',
              }),
        );
      }
    }

    return parts.length > 0
      ? parts.join(' • ')
      : gt('No filters', {
          context: 'shared link filter summary empty',
        });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5" />
              <T context="shared links manager dialog title">Shared Links</T>
            </DialogTitle>
            <DialogDescription>
              {isAdminUser ? (
                <T context="shared links manager dialog admin">
                  Manage all shared links for &quot;<Var>{reportTitle}</Var>
                  &quot;
                </T>
              ) : (
                <T context="shared links manager dialog">
                  Manage shared links for &quot;<Var>{reportTitle}</Var>&quot;
                </T>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && links.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>
                  <T context="shared links empty state">No shared links yet</T>
                </p>
                <p className="text-sm mt-1">
                  <T context="shared links empty state">
                    Create a link to share this report with others.
                  </T>
                </p>
              </div>
            )}

            {!isLoading &&
              links.map((link) => (
                <SharedLinkCard
                  key={link.id}
                  link={link}
                  onCopy={() => handleCopyLink(link)}
                  onEdit={() => setEditingLink(link)}
                  onDeleteRequest={() => setConfirmDeleteId(link.id)}
                  onDeleteCancel={() => setConfirmDeleteId(null)}
                  isCopied={copiedId === link.id}
                  isConfirmingDelete={confirmDeleteId === link.id}
                  formatMetadata={formatMetadata}
                  showCreator={isAdminUser}
                />
              ))}
          </div>

          <DialogFooter className="flex-row justify-between sm:justify-between">
            <Button variant="outline" onClick={onClose}>
              <T context="dialog button">Close</T>
            </Button>
            <Button onClick={onCreateNew}>
              <T context="shared links manager">Create New Link</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editingLink && (
        <EditShareableLinkModal
          open={!!editingLink}
          onClose={() => setEditingLink(null)}
          link={editingLink}
        />
      )}
    </>
  );
}

interface SharedLinkCardProps {
  link: ShareableLinkWithCreator;
  onCopy: () => void;
  onEdit: () => void;
  onDeleteRequest: () => void;
  onDeleteCancel: () => void;
  isCopied: boolean;
  isConfirmingDelete: boolean;
  formatMetadata: (metadata: Record<string, unknown>) => string;
  showCreator: boolean;
}

function SharedLinkCard({
  link,
  onCopy,
  onEdit,
  onDeleteRequest,
  onDeleteCancel,
  isCopied,
  isConfirmingDelete,
  formatMetadata,
  showCreator,
}: SharedLinkCardProps) {
  const gt = useGT();
  const deleteLink = useDeleteShareableLink(link.id);

  async function handleConfirmDelete() {
    await deleteLink.mutateAsync(undefined as unknown as void);
    onDeleteCancel();
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            {/* Link Name */}
            <div className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              {link.name ? (
                <span className="font-medium text-sm">{link.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground italic">
                  <T context="shared link without name">Unnamed</T>
                </span>
              )}
            </div>

            {/* Emails */}
            <div className="flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex flex-wrap gap-1">
                {link.allowedEmails.slice(0, 3).map((email) => (
                  <Badge key={email} variant="secondary" className="text-xs">
                    {email}
                  </Badge>
                ))}
                {link.allowedEmails.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    <T context="shared link more emails badge">
                      +<Var>{link.allowedEmails.length - 3}</Var> more
                    </T>
                  </Badge>
                )}
              </div>
            </div>

            {/* Metadata/Filters */}
            <div className="text-sm text-muted-foreground">
              {formatMetadata(link.metadata)}
            </div>

            {/* Created date */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T context="shared link created relative time">
                Created{' '}
                <Var>
                  {formatDistanceToNow(new Date(link.createdAt), {
                    addSuffix: true,
                  })}
                </Var>
              </T>
              {showCreator && link.createdByUser && (
                <T context="shared link creator">
                  {' '}
                  • by <Var>{link.createdByUser.name}</Var>
                </T>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {isConfirmingDelete ? (
              // Inline delete confirmation
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  <T context="confirm delete shared link">Delete?</T>
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleConfirmDelete}
                  disabled={deleteLink.isPending}
                >
                  {deleteLink.isPending ? (
                    <CircleNotchIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <T context="confirm delete yes">Yes</T>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDeleteCancel}
                  disabled={deleteLink.isPending}
                >
                  <T context="confirm delete no">No</T>
                </Button>
              </div>
            ) : (
              // Normal actions
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          const url = getShareableLinkUrl(link.token);
                          window.open(url, '_blank');
                        }}
                      >
                        <ArrowSquareOutIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {gt('Open link', {
                        context: 'shared link action tooltip',
                      })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={onCopy}
                      >
                        {isCopied ? (
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        ) : (
                          <CopyIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isCopied
                        ? gt('Copied!', {
                            context: 'copy link success tooltip',
                          })
                        : gt('Copy link', {
                            context: 'copy shared link tooltip',
                          })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={onEdit}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {gt('Edit link', {
                        context: 'shared link action tooltip',
                      })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={onDeleteRequest}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {gt('Delete link', {
                        context: 'shared link action tooltip',
                      })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
